/**
 * Modelo de formulario y validaciones de Tarifas (US-2.4) — Admin Web.
 *
 * La red y el contrato viven en `tariffApi`/`@goelectric/shared-types`; acá está el
 * estado editable de la vista, las reglas de negocio del formulario (por tipo de
 * tarifa) y la conversión al body del contrato.
 *
 * Reglas acordadas con la US 2.4:
 * - FLAT: al menos un componente de costo.
 * - TOU: al menos un periodo con `HH:mm` válido y precio.
 * - IDLE_FEE: un componente de tipo `IDLE_FEE`.
 * - `validTo >= validFrom`, precios >= 0 e IVA entre 0 y 100.
 *
 * Gap del contrato (documentado, no inventado): `minCostCop`/`maxCostCop` existen en
 * `Tariff` pero **no** en `CreateTariffBody`/`UpdateTariffBody`, así que no se editan.
 * `type`/`currency` tampoco viajan en el PATCH (solo se fijan al crear).
 */
import type {
  CreateTariffBody,
  Tariff,
  TariffComponent,
  TariffComponentType,
  TariffType,
  TouPeriod,
  UpdateTariffBody,
} from '@goelectric/shared-types';

/* ========================================================================= */
/* Estado del formulario                                                     */
/* ========================================================================= */

/** Componente editable: los numéricos admiten `null` mientras el usuario no escribe. */
export interface TariffComponentFormValue {
  type?: TariffComponentType;
  priceCop?: number | null;
  vatRatePct?: number | null;
  stepSize?: number | null;
  description?: string;
}

/** Periodo TOU editable. */
export interface TouPeriodFormValue {
  label?: string;
  startTime?: string;
  endTime?: string;
  priceCopPerKwh?: number | null;
}

/** Valores del formulario (fechas en formato `datetime-local`, hora local del navegador). */
export interface TariffFormValues {
  name: string;
  description?: string;
  type: TariffType;
  validFrom?: string;
  validTo?: string;
  components: TariffComponentFormValue[];
  touPeriods: TouPeriodFormValue[];
  publishedToOcpi: boolean;
}

/** IVA por defecto de una fila nueva (19 % en Colombia). */
export const DEFAULT_VAT_RATE_PCT = 19;

/** Moneda única del backoffice (el contrato la fija en COP de forma implícita). */
export const TARIFF_CURRENCY = 'COP';

/** Fila inicial de componente para que el formulario no arranque vacío. */
export function defaultComponent(type: TariffComponentType = 'FLAT'): TariffComponentFormValue {
  return {
    type,
    priceCop: null,
    vatRatePct: DEFAULT_VAT_RATE_PCT,
    stepSize: type === 'ENERGY' ? 1 : undefined,
  };
}

/** Estado inicial del alta: FLAT con un componente listo para completar. */
export function emptyTariffFormValues(): TariffFormValues {
  return {
    name: '',
    description: '',
    type: 'FLAT',
    validFrom: undefined,
    validTo: undefined,
    components: [defaultComponent('FLAT')],
    touPeriods: [],
    publishedToOcpi: true,
  };
}

/** Convierte un ISO del backend a `YYYY-MM-DDTHH:mm` (valor de `datetime-local`). */
export function toDateTimeLocalValue(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return undefined;
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

/** Convierte un `datetime-local` a ISO 8601 (UTC); inválido/vacío → `undefined`. */
export function dateTimeLocalToIso(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

/** Mapea la entidad del contrato al estado del formulario de edición. */
export function tariffToFormValues(tariff: Tariff): TariffFormValues {
  return {
    name: tariff.name,
    description: tariff.description ?? '',
    type: tariff.type,
    validFrom: toDateTimeLocalValue(tariff.validFrom),
    validTo: toDateTimeLocalValue(tariff.validTo),
    components: tariff.components.map((component) => ({
      type: component.type,
      priceCop: component.priceCop,
      vatRatePct: component.vatRatePct,
      stepSize: component.stepSize,
      description: component.description ?? '',
    })),
    touPeriods: (tariff.touPeriods ?? []).map((period) => ({
      label: period.label,
      startTime: period.startTime,
      endTime: period.endTime,
      priceCopPerKwh: period.priceCopPerKwh,
    })),
    publishedToOcpi: tariff.publishedToOcpi ?? true,
  };
}

/* ========================================================================= */
/* Conversión al body del contrato                                           */
/* ========================================================================= */

function toComponentBody(component: TariffComponentFormValue): TariffComponent {
  const description = component.description?.trim();
  return {
    type: component.type ?? 'FLAT',
    priceCop: component.priceCop ?? 0,
    vatRatePct: component.vatRatePct ?? 0,
    ...(component.stepSize !== undefined && component.stepSize !== null
      ? { stepSize: component.stepSize }
      : {}),
    ...(description ? { description } : {}),
  };
}

function toTouPeriodBody(period: TouPeriodFormValue): TouPeriod {
  return {
    label: period.label?.trim() ?? '',
    startTime: period.startTime ?? '',
    endTime: period.endTime ?? '',
    priceCopPerKwh: period.priceCopPerKwh ?? 0,
  };
}

function commonBody(values: TariffFormValues) {
  const description = values.description?.trim();
  const validFrom = dateTimeLocalToIso(values.validFrom);
  const validTo = dateTimeLocalToIso(values.validTo);
  return {
    name: values.name.trim(),
    ...(description ? { description } : {}),
    ...(validFrom ? { validFrom } : {}),
    ...(validTo ? { validTo } : {}),
    components: values.components.map(toComponentBody),
    ...(values.type === 'TOU' && values.touPeriods.length > 0
      ? { touPeriods: values.touPeriods.map(toTouPeriodBody) }
      : {}),
    publishedToOcpi: values.publishedToOcpi,
  };
}

/** Body de `POST /api/v1/tariffs` (incluye tipo y moneda COP). */
export function toCreateTariffBody(values: TariffFormValues): CreateTariffBody {
  return {
    ...commonBody(values),
    currency: TARIFF_CURRENCY,
    type: values.type,
  };
}

/**
 * Body de `PATCH /api/v1/tariffs/{tariffId}`. El contrato no permite cambiar `type`
 * ni `currency`; `validTo` se envía `null` cuando el usuario lo deja vacío.
 */
export function toUpdateTariffBody(values: TariffFormValues): UpdateTariffBody {
  const body = commonBody(values);
  const validTo = dateTimeLocalToIso(values.validTo);
  return {
    ...body,
    // El PATCH debe poder limpiar la descripción: se envía siempre (vacío → '').
    description: values.description?.trim() ?? '',
    validTo: validTo ?? null,
  };
}

/* ========================================================================= */
/* Validaciones                                                              */
/* ========================================================================= */

/** `HH:mm` en 24 h (00:00–23:59). */
export const HHMM_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

/** `true` si el texto es una hora válida en formato `HH:mm`. */
export function isValidHhMm(value: unknown): boolean {
  return typeof value === 'string' && HHMM_PATTERN.test(value);
}

/** Problema de validación asociado a un campo (ruta estilo `components[0].priceCop`). */
export interface TariffValidationIssue {
  field: string;
  message: string;
}

/** Path de antd (`NamePath`) derivado de una ruta como `components[0].priceCop`. */
export type TariffFieldPath = (string | number)[];

/** Convierte rutas con corchetes a paths que `form.setFields` entiende. */
export function toAntdFieldPath(field: string): TariffFieldPath {
  return (field.match(/[^.[\]]+/g) ?? []).map((part) =>
    /^\d+$/.test(part) ? Number(part) : part,
  );
}

/** Valida un componente aislado (precio, IVA y paso). */
function validateComponent(
  component: TariffComponentFormValue,
  index: number,
): TariffValidationIssue[] {
  const issues: TariffValidationIssue[] = [];
  const base = `components[${index}]`;

  if (!component.type) {
    issues.push({ field: `${base}.type`, message: 'Elige el tipo de componente.' });
  }
  if (component.priceCop === undefined || component.priceCop === null) {
    issues.push({ field: `${base}.priceCop`, message: 'Ingresa el precio del componente.' });
  } else if (component.priceCop < 0) {
    issues.push({ field: `${base}.priceCop`, message: 'El precio debe ser mayor o igual a 0.' });
  }
  if (component.vatRatePct === undefined || component.vatRatePct === null) {
    issues.push({ field: `${base}.vatRatePct`, message: 'Ingresa el IVA del componente.' });
  } else if (component.vatRatePct < 0 || component.vatRatePct > 100) {
    issues.push({ field: `${base}.vatRatePct`, message: 'El IVA debe estar entre 0 y 100.' });
  }
  if (
    component.stepSize !== undefined &&
    component.stepSize !== null &&
    component.stepSize < 0.001
  ) {
    issues.push({
      field: `${base}.stepSize`,
      message: 'El tamaño del paso debe ser mayor o igual a 0.001.',
    });
  }
  return issues;
}

/** Valida un periodo TOU (nombre, horas `HH:mm` y precio). */
function validateTouPeriod(period: TouPeriodFormValue, index: number): TariffValidationIssue[] {
  const issues: TariffValidationIssue[] = [];
  const base = `touPeriods[${index}]`;
  const name = period.label?.trim() || `Periodo ${index + 1}`;

  if (!period.label?.trim()) {
    issues.push({ field: `${base}.label`, message: 'Escribe un nombre para el periodo.' });
  }
  if (!isValidHhMm(period.startTime)) {
    issues.push({
      field: `${base}.startTime`,
      message: `El periodo «${name}» necesita una hora de inicio válida (HH:mm).`,
    });
  }
  if (!isValidHhMm(period.endTime)) {
    issues.push({
      field: `${base}.endTime`,
      message: `El periodo «${name}» necesita una hora de fin válida (HH:mm).`,
    });
  } else if (isValidHhMm(period.startTime) && period.endTime === period.startTime) {
    issues.push({
      field: `${base}.endTime`,
      message: `El periodo «${name}» necesita una hora de fin distinta de la hora de inicio.`,
    });
  }
  if (period.priceCopPerKwh === undefined || period.priceCopPerKwh === null) {
    issues.push({
      field: `${base}.priceCopPerKwh`,
      message: 'Ingresa el precio por kWh del periodo.',
    });
  } else if (period.priceCopPerKwh < 0) {
    issues.push({
      field: `${base}.priceCopPerKwh`,
      message: 'El precio por kWh debe ser mayor o igual a 0.',
    });
  }
  return issues;
}

/** Valida el conjunto de componentes según el tipo de tarifa (mensaje único para antd). */
export function validateComponentSet(
  type: TariffType,
  components: TariffComponentFormValue[] | undefined,
): string | null {
  if (!components || components.length === 0) {
    return 'Agrega al menos un componente de costo.';
  }
  if (type === 'IDLE_FEE' && !components.some((component) => component.type === 'IDLE_FEE')) {
    return 'Agrega un componente de tipo Idle Fee.';
  }
  return null;
}

/** Valida el conjunto de periodos TOU (solo aplica a tarifas TOU). */
export function validateTouPeriodSet(
  type: TariffType,
  periods: TouPeriodFormValue[] | undefined,
): string | null {
  if (type !== 'TOU') return null;
  if (!periods || periods.length === 0) {
    return 'Agrega al menos un periodo TOU.';
  }
  return null;
}

/** `true` si `validTo` no es anterior a `validFrom` (ambos opcionales). */
export function isValidVigencia(
  validFrom: string | undefined,
  validTo: string | undefined,
): boolean {
  if (!validFrom || !validTo) return true;
  const from = new Date(validFrom).getTime();
  const to = new Date(validTo).getTime();
  if (Number.isNaN(from) || Number.isNaN(to)) return true;
  return to >= from;
}

/**
 * Valida el formulario completo. Devuelve la lista de problemas con su campo; usada
 * por los tests de reglas por tipo y como red de seguridad antes de enviar.
 */
export function validateTariffFormValues(values: TariffFormValues): TariffValidationIssue[] {
  const issues: TariffValidationIssue[] = [];

  if (!values.name?.trim()) {
    issues.push({ field: 'name', message: 'Escribe el nombre de la tarifa.' });
  }

  const componentMessage = validateComponentSet(values.type, values.components);
  if (componentMessage) {
    issues.push({ field: 'components', message: componentMessage });
  }
  values.components.forEach((component, index) => {
    issues.push(...validateComponent(component, index));
  });

  if (values.type === 'TOU') {
    const periodMessage = validateTouPeriodSet(values.type, values.touPeriods);
    if (periodMessage) {
      issues.push({ field: 'touPeriods', message: periodMessage });
    }
    (values.touPeriods ?? []).forEach((period, index) => {
      issues.push(...validateTouPeriod(period, index));
    });
  }

  if (!isValidVigencia(values.validFrom, values.validTo)) {
    issues.push({
      field: 'validTo',
      message: 'La fecha de fin debe ser igual o posterior a la fecha de inicio.',
    });
  }

  return issues;
}
